import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState, useLayoutEffect, useCallback } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CommonTable from "../../../common_components/CommonTable";
import CommonChip from "../../../common_components/CommonChip";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { TABLE_TYPES } from "../../../common_components/tableConstants";
import { STATUS_CHIP } from "../utils";
import { formatDate, getStatusLabel, getTypeLabel, withNA, NA_TEXT } from "./shared";
// Faded + italic styling for empty/"N/A" values, matching other modules (Email Config etc.).
const naClass = (value) => (value === NA_TEXT ? " outreach-tracker-na-text" : "");
// Tracker names share a fixed-width column. The name shows in full when it fits;
// only when it actually overflows the cell does an eye button appear (full value on hover).
const TrackerNameCell = ({ name }) => {
    const value = (name || "").trim();
    const nameRef = useRef(null);
    const [overflowing, setOverflowing] = useState(false);
    useLayoutEffect(() => {
        const el = nameRef.current;
        if (!el)
            return;
        const measure = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [value]);
    if (!value)
        return _jsx(Typography, { className: "outreach-tracker-cell-text outreach-tracker-na-text", children: withNA(value) });
    return (_jsxs(Box, { className: "outreach-tracker-tracker-cell", children: [_jsx(Typography, { ref: nameRef, className: "outreach-tracker-tracker-name", children: value }), overflowing && (_jsx(HoverTooltip, { title: value, children: _jsx("img", { src: "/assets/eye.svg", alt: "view full name", className: "outreach-tracker-eye" }) }))] }));
};
const OutreachTrackerMap = ({ rows, loading, isFirstLoad = false, searchText, onEdit, onDelete, onNotes, sortConfig, onSortChange, hiddenColumns, onColumnVisibilityChange, columnVisibilityAnchor, onColumnVisibilityClose, columnOrder, onColumnOrderChange, canManage = false, }) => {
    const columns = useMemo(() => [
        { id: "client", label: "Client", width: "160px", align: "left", sortable: true },
        { id: "am", label: "AM", width: "150px", align: "left", sortable: true },
        { id: "os", label: "OS", width: "150px", align: "left", sortable: true },
        {
            id: "trackerStatus",
            label: "Tracker Status",
            align: "center",
            children: [
                { id: "type", label: "Type", width: "110px", align: "left", sortable: true },
                { id: "tracker", label: "Tracker", width: "180px", align: "left", sortable: true },
                { id: "launch_date", label: "Launch Date", width: "130px", align: "left", sortable: true },
                { id: "end_date", label: "End Date", width: "130px", align: "left", sortable: true },
                { id: "status", label: "Status", width: "140px", align: "left", sortable: true },
            ],
        },
        {
            id: "trackerInfo",
            label: "Tracker Info",
            align: "center",
            children: [
                { id: "keywords", label: "Keywords", width: "100px", align: "center", sortable: false, showColumnActions: false },
                { id: "pitch", label: "Pitch", width: "100px", align: "center", sortable: false, showColumnActions: false },
                { id: "tracker_link", label: "Tracker", width: "100px", align: "center", sortable: false, showColumnActions: false },
            ],
        },
        {
            id: "contactInfo",
            label: "Contact Info",
            align: "center",
            children: [
                { id: "c_total", label: "Total", width: "110px", align: "left", sortable: true },
                { id: "c_enrolled", label: "Enrolled", width: "110px", align: "left", sortable: true },
                { id: "c_unenrolled", label: "Unenrolled", width: "120px", align: "left", sortable: true },
                { id: "c_bounced", label: "Bounced", width: "110px", align: "left", sortable: true },
                { id: "c_sent", label: "Sent", width: "100px", align: "left", sortable: true },
            ],
        },
        {
            id: "leadInfo",
            label: "Lead Info",
            align: "center",
            children: [
                { id: "l_total", label: "Total", width: "110px", align: "left", sortable: true },
                { id: "l_positive", label: "Positive", width: "110px", align: "left", sortable: true },
                { id: "l_negative", label: "Negative", width: "110px", align: "left", sortable: true },
                { id: "l_neutral", label: "Neutral", width: "110px", align: "left", sortable: true },
                { id: "post_mortem", label: "Post Mortem", width: "120px", align: "center", sortable: true },
            ],
        },
        ...(canManage
            ? [{ id: "actions", label: "Action", width: "130px", align: "center", sortable: false, showColumnActions: false, draggable: false }]
            : []),
    ], [canManage]);
    // The shared column panel's "Reset Order" returns only the standalone columns and drops
    // the group ids, which makes the table sort the groups to the end and push "Action" into
    // the middle. A genuine drag always carries the group ids. So when the incoming order has
    // no group id, treat it as a reset and clear the custom order — the table then falls back
    // to its natural (original) column order.
    const groupIds = useMemo(() => columns.filter((c) => Array.isArray(c.children) && c.children.length > 0).map((c) => c.id), [columns]);
    const handleColumnOrderChange = useCallback((order) => {
        if (!onColumnOrderChange)
            return;
        const isResetShape = !order.some((id) => groupIds.includes(id));
        onColumnOrderChange(isResetShape ? null : order);
    }, [onColumnOrderChange, groupIds]);
    const renderLinkCell = (value, alt) => (_jsx(Box, { className: "outreach-tracker-keywords-cell", children: value ? (_jsx(HoverTooltip, { title: value, children: _jsxs("a", { href: /^https?:\/\//.test(value) ? value : undefined, target: "_blank", rel: "noreferrer", onClick: (e) => e.stopPropagation(), className: "outreach-tracker-link", children: [_jsx("img", { src: "/assets/link.svg", alt: alt }), _jsx("span", { className: "outreach-tracker-link-dot" })] }) })) : (_jsx("img", { src: "/assets/link.svg", alt: alt, className: "outreach-tracker-link-muted" })) }));
    const renderNumberCell = (value) => (_jsx(Typography, { className: "outreach-tracker-cell-text", children: value == null ? "-" : value }));
    const renderTrackerNameCell = (name) => _jsx(TrackerNameCell, { name: name });
    const renderCell = (row, column) => {
        switch (column.id) {
            case "client": {
                const v = withNA(row.client?.name);
                return _jsx(Typography, { className: `outreach-tracker-cell-text${naClass(v)}`, children: v });
            }
            case "am": {
                const v = withNA((row.am || []).map((a) => a.name).filter(Boolean).join(", "));
                // AM can hold multiple managers — let it wrap so every name is visible.
                return _jsx(Typography, { className: `outreach-tracker-cell-text outreach-tracker-am-cell${naClass(v)}`, children: v });
            }
            case "os": {
                const v = withNA(row.os?.name);
                return _jsx(Typography, { className: `outreach-tracker-cell-text${naClass(v)}`, children: v });
            }
            case "type": {
                const v = getTypeLabel(row.type);
                return _jsx(Typography, { className: `outreach-tracker-cell-text${naClass(v)}`, children: v });
            }
            case "tracker":
                return renderTrackerNameCell(row.tracker_name);
            case "launch_date": {
                const v = formatDate(row.launch_date);
                return _jsx(Typography, { className: `outreach-tracker-cell-text${naClass(v)}`, children: v });
            }
            case "end_date": {
                const v = formatDate(row.end_date);
                return _jsx(Typography, { className: `outreach-tracker-cell-text${naClass(v)}`, children: v });
            }
            case "status":
                return (_jsx(CommonChip, { label: getStatusLabel(row.status), renderType: "tag", index: STATUS_CHIP[row.status] ?? 5, sx: { fontSize: '14px', fontWeight: 500 } }));
            case "keywords":
                return renderLinkCell(row.keywords, "keywords");
            case "pitch":
                return renderLinkCell(row.pitch, "pitch");
            case "tracker_link":
                return renderLinkCell(row.tracker, "tracker");
            case "c_total":
                return renderNumberCell(row.contact_info?.total);
            case "c_enrolled":
                return renderNumberCell(row.contact_info?.enrolled);
            case "c_unenrolled":
                return renderNumberCell(row.contact_info?.unenrolled);
            case "c_bounced":
                return renderNumberCell(row.contact_info?.bounced);
            case "c_sent":
                return renderNumberCell(row.contact_info?.sent);
            case "l_total":
                return renderNumberCell(row.lead_info?.total);
            case "l_positive":
                return renderNumberCell(row.lead_info?.positive);
            case "l_negative":
                return renderNumberCell(row.lead_info?.negative);
            case "l_neutral":
                return renderNumberCell(row.lead_info?.neutral);
            case "post_mortem":
                return renderLinkCell(row.post_mortem, "post mortem");
            case "actions":
                return (_jsxs(Box, { className: "outreach-tracker-actions-cell", children: [_jsx(HoverTooltip, { title: "Edit", children: _jsx(IconButton, { className: "outreach-tracker-action-button", size: "small", onClick: (e) => {
                                    e.stopPropagation();
                                    onEdit(row);
                                }, children: _jsx("img", { src: "/assets/edit.svg", alt: "edit" }) }) }), _jsx(HoverTooltip, { title: "Notes", children: _jsx(IconButton, { className: "outreach-tracker-action-button", size: "small", onClick: (e) => {
                                    e.stopPropagation();
                                    onNotes(row);
                                }, children: _jsxs("span", { className: "outreach-tracker-note-icon", children: [_jsx("img", { src: "/assets/note-gray.svg", alt: "notes" }), row.notes?.trim() && _jsx("span", { className: "outreach-tracker-note-dot" })] }) }) }), _jsx(HoverTooltip, { title: "Delete", children: _jsx(IconButton, { className: "outreach-tracker-action-button delete", size: "small", onClick: (e) => {
                                    e.stopPropagation();
                                    onDelete(row);
                                }, children: _jsx("img", { src: "/assets/delete.svg", alt: "delete" }) }) })] }));
            default:
                return null;
        }
    };
    return (_jsx(Box, { className: "outreach-tracker-table-box", children: _jsx(CommonTable, { columns: columns, rows: rows, renderCell: renderCell, expandTrigger: "none", type: TABLE_TYPES.GROUPED, tableHeadClassName: "reusable-table-header-label all-Font-Sizes", emptyMessage: searchText ? "No outreach trackers found matching your search" : "No outreach trackers found", isLoading: loading, isFirstLoad: isFirstLoad, sortConfig: sortConfig, onSortChange: onSortChange, columnVisibilityConfig: {
                enable: true,
                columns: [
                    "client", "am", "os",
                    "type", "tracker", "launch_date", "end_date", "status",
                    "keywords", "pitch", "tracker_link",
                    "c_total", "c_enrolled", "c_unenrolled", "c_bounced", "c_sent",
                    "l_total", "l_positive", "l_negative", "l_neutral", "post_mortem",
                    ...(canManage ? ["actions"] : []),
                ],
                enableReorder: true,
            }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: onColumnVisibilityChange, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: onColumnVisibilityClose, columnOrder: columnOrder, onColumnOrderChange: handleColumnOrderChange, stickyHeader: true, maxHeight: "100%" }) }));
};
export default OutreachTrackerMap;
